import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";

// Types for local mock data
export interface RawMaterial {
  id: number;
  materialCode: string;
  name: string;
  description: string;
  category: 'aggregate' | 'cite-cementitious' | 'liquid' | 'admixture';
  unit: string;
  currentQuantity: number;
  reorderPoint: number;
  unitCost: number;
  supplier?: string;
  lastUpdated: string;
}

export interface MaterialLocation {
  id: number;
  name: string;
  description: string;
  type: 'silo' | 'tank' | 'yard' | 'warehouse';
}

export interface InventoryRecord {
  id: number;
  materialId: number;
  locationId: number;
  quantity: number;
  lotNumber?: string;
  receivedDate: string;
  expirationDate?: string;
  cost: number;
}

interface FormData {
  // Basic Info
  materialCode: string;
  name: string;
  description: string;
  category: string;
  unit: string;

  // Inventory
  initialQuantity: number;
  locationId: string;
  lotNumber: string;
  expirationDate: string;
  unitCost: number;
  notes: string;
}

// Mock ready-mix materials
const INITIAL_MATERIALS: RawMaterial[] = [
  // Aggregates
  { id: 1, materialCode: 'AGG-SAND', name: 'Sand (Fine Aggregate)', description: 'Fine aggregate for concrete mixes', category: 'aggregate', unit: 'tons', currentQuantity: 850, reorderPoint: 200, unitCost: 25, supplier: 'ABC Aggregates', lastUpdated: new Date().toISOString() },
  { id: 2, materialCode: 'AGG-GRAVEL', name: 'Gravel (Coarse Aggregate)', description: 'Coarse aggregate 3/4"', category: 'aggregate', unit: 'tons', currentQuantity: 1200, reorderPoint: 300, unitCost: 30, supplier: 'ABC Aggregates', lastUpdated: new Date().toISOString() },
  { id: 3, materialCode: 'AGG-STONE', name: 'Crushed Stone', description: '3/4" crushed limestone', category: 'aggregate', unit: 'tons', currentQuantity: 600, reorderPoint: 150, unitCost: 35, supplier: 'Midwest Stone', lastUpdated: new Date().toISOString() },

  // Cementitious
  { id: 4, materialCode: 'CEM-T1', name: 'Cement Type I/II', description: 'Portland cement Type I/II', category: 'cite-cementitious', unit: 'tons', currentQuantity: 450, reorderPoint: 100, unitCost: 120, supplier: 'Lehigh Cement', lastUpdated: new Date().toISOString() },
  { id: 5, materialCode: 'CEM-T3', name: 'Cement Type III', description: 'High early strength cement', category: 'cite-cementitious', unit: 'tons', currentQuantity: 120, reorderPoint: 50, unitCost: 135, supplier: 'Lehigh Cement', lastUpdated: new Date().toISOString() },
  { id: 6, materialCode: 'CEM-FA', name: 'Fly Ash', description: 'Class F fly ash', category: 'cite-cementitious', unit: 'tons', currentQuantity: 200, reorderPoint: 75, unitCost: 65, supplier: 'Boral Materials', lastUpdated: new Date().toISOString() },
  { id: 7, materialCode: 'CEM-SLAG', name: 'Slag', description: 'Ground granulated blast furnace slag', category: 'cite-cementitious', unit: 'tons', currentQuantity: 180, reorderPoint: 60, unitCost: 85, supplier: 'Holcim', lastUpdated: new Date().toISOString() },

  // Liquids
  { id: 8, materialCode: 'LIQ-WATER', name: 'Water', description: 'Potable water for mixing', category: 'liquid', unit: 'gallons', currentQuantity: 50000, reorderPoint: 10000, unitCost: 0.005, supplier: 'City Water', lastUpdated: new Date().toISOString() },

  // Admixtures
  { id: 9, materialCode: 'ADM-AIR', name: 'Air Entrainer', description: 'Air entraining admixture', category: 'admixture', unit: 'gallons', currentQuantity: 550, reorderPoint: 100, unitCost: 8.50, supplier: 'BASF', lastUpdated: new Date().toISOString() },
  { id: 10, materialCode: 'ADM-WR', name: 'Water Reducer', description: 'Mid-range water reducing admixture', category: 'admixture', unit: 'gallons', currentQuantity: 420, reorderPoint: 100, unitCost: 12.00, supplier: 'BASF', lastUpdated: new Date().toISOString() },
  { id: 11, materialCode: 'ADM-ACC', name: 'Accelerator', description: 'Non-chloride accelerator', category: 'admixture', unit: 'gallons', currentQuantity: 280, reorderPoint: 75, unitCost: 15.00, supplier: 'Sika', lastUpdated: new Date().toISOString() },
  { id: 12, materialCode: 'ADM-RET', name: 'Retarder', description: 'Set retarding admixture', category: 'admixture', unit: 'gallons', currentQuantity: 320, reorderPoint: 75, unitCost: 14.00, supplier: 'Sika', lastUpdated: new Date().toISOString() },
  { id: 13, materialCode: 'ADM-SP', name: 'Superplasticizer', description: 'High-range water reducer', category: 'admixture', unit: 'gallons', currentQuantity: 180, reorderPoint: 50, unitCost: 22.00, supplier: 'GCP Applied Tech', lastUpdated: new Date().toISOString() },
];

const INITIAL_LOCATIONS: MaterialLocation[] = [
  { id: 1, name: 'Silo 1', description: 'Cement Type I/II storage', type: 'silo' },
  { id: 2, name: 'Silo 2', description: 'Cement Type III storage', type: 'silo' },
  { id: 3, name: 'Silo 3', description: 'Fly Ash storage', type: 'silo' },
  { id: 4, name: 'Sand Pile A', description: 'Primary sand storage', type: 'yard' },
  { id: 5, name: 'Gravel Pile A', description: 'Primary gravel storage', type: 'yard' },
  { id: 6, name: 'Stone Pile A', description: 'Crushed stone storage', type: 'yard' },
  { id: 7, name: 'Admixture Tank 1', description: 'Air entrainer & water reducer', type: 'tank' },
  { id: 8, name: 'Admixture Tank 2', description: 'Accelerator & retarder', type: 'tank' },
  { id: 9, name: 'Water Tank', description: 'Main water reservoir', type: 'tank' },
  { id: 10, name: 'Warehouse', description: 'General storage', type: 'warehouse' },
];

class AddRawMaterialsWorkflowStore {
  rootStore: RootStore;

  // Local Mock Data
  materials: RawMaterial[] = [...INITIAL_MATERIALS];
  locations: MaterialLocation[] = [...INITIAL_LOCATIONS];
  inventoryHistory: InventoryRecord[] = [];

  // UI State
  activeStep = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Material Selection State
  isNewMaterial = false;
  selectedExistingMaterial: RawMaterial | null = null;

  // Form Data
  formData: FormData = {
    materialCode: "",
    name: "",
    description: "",
    category: "aggregate",
    unit: "tons",
    initialQuantity: 0,
    locationId: "",
    lotNumber: "",
    expirationDate: "",
    unitCost: 0,
    notes: "",
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // Actions
  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    this.activeStep++;
  };

  prevStep = () => {
    this.activeStep--;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setIsNewMaterial = (isNew: boolean) => {
    this.isNewMaterial = isNew;
    if (isNew) {
      this.selectedExistingMaterial = null;
      this.formData.materialCode = "";
      this.formData.name = "";
      this.formData.description = "";
      this.formData.category = "aggregate";
      this.formData.unit = "tons";
    }
  };

  selectExistingMaterial = (material: RawMaterial | null) => {
    this.selectedExistingMaterial = material;
    if (material) {
      this.formData.materialCode = material.materialCode;
      this.formData.name = material.name;
      this.formData.description = material.description;
      this.formData.category = material.category;
      this.formData.unit = material.unit;
      this.formData.unitCost = material.unitCost;
    }
  };

  updateFormData = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    this.formData[field] = value;
  };

  // Load initial data (no-op for mock data, but keeps API compatible)
  loadInitialData = async () => {
    this.loading = true;
    // Simulate brief loading
    await new Promise(resolve => setTimeout(resolve, 300));
    runInAction(() => {
      this.loading = false;
    });
  };

  // Submit - adds material or updates quantity locally
  submitMaterial = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      runInAction(() => {
        if (this.isNewMaterial) {
          // Create new material
          const newId = Math.max(...this.materials.map(m => m.id)) + 1;
          const newMaterial: RawMaterial = {
            id: newId,
            materialCode: this.formData.materialCode,
            name: this.formData.name,
            description: this.formData.description,
            category: this.formData.category as RawMaterial['category'],
            unit: this.formData.unit,
            currentQuantity: this.formData.initialQuantity,
            reorderPoint: 50,
            unitCost: this.formData.unitCost,
            lastUpdated: new Date().toISOString(),
          };
          this.materials.push(newMaterial);

          this.success = `Material "${this.formData.name}" has been created with ${this.formData.initialQuantity} ${this.formData.unit} initial stock.`;
        } else if (this.selectedExistingMaterial) {
          // Update existing material quantity
          const material = this.materials.find(m => m.id === this.selectedExistingMaterial!.id);
          if (material) {
            material.currentQuantity += this.formData.initialQuantity;
            material.lastUpdated = new Date().toISOString();
          }

          // Add to inventory history
          this.inventoryHistory.push({
            id: this.inventoryHistory.length + 1,
            materialId: this.selectedExistingMaterial.id,
            locationId: parseInt(this.formData.locationId) || 1,
            quantity: this.formData.initialQuantity,
            lotNumber: this.formData.lotNumber,
            receivedDate: new Date().toISOString(),
            expirationDate: this.formData.expirationDate || undefined,
            cost: this.formData.unitCost,
          });

          this.success = `Added ${this.formData.initialQuantity} ${this.formData.unit} of "${this.selectedExistingMaterial.name}" to inventory.`;
        }

        this.loading = false;
        this.nextStep();
      });
    } catch (err: any) {
      runInAction(() => {
        this.error = err.message || "Failed to process material";
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.activeStep = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.isNewMaterial = false;
    this.selectedExistingMaterial = null;
    this.formData = {
      materialCode: "",
      name: "",
      description: "",
      category: "aggregate",
      unit: "tons",
      initialQuantity: 0,
      locationId: "",
      lotNumber: "",
      expirationDate: "",
      unitCost: 0,
      notes: "",
    };
  };

  // Computed Values
  get canProceedFromStep1() {
    if (this.isNewMaterial) {
      return (
        !!this.formData.materialCode &&
        !!this.formData.name &&
        !!this.formData.category &&
        !!this.formData.unit
      );
    } else {
      return !!this.selectedExistingMaterial;
    }
  }

  get canProceedFromStep2() {
    return this.formData.initialQuantity > 0 && !!this.formData.locationId;
  }

  get materialsByCategory() {
    return {
      aggregate: this.materials.filter(m => m.category === 'aggregate'),
      'cite-cementitious': this.materials.filter(m => m.category === 'cite-cementitious'),
      liquid: this.materials.filter(m => m.category === 'liquid'),
      admixture: this.materials.filter(m => m.category === 'admixture'),
    };
  }

  get totalInventoryValue() {
    return this.materials.reduce((sum, m) => sum + (m.currentQuantity * m.unitCost), 0);
  }

  get lowStockMaterials() {
    return this.materials.filter(m => m.currentQuantity <= m.reorderPoint);
  }
}

export default AddRawMaterialsWorkflowStore;
